/*
 * Słodki Bufet cart engine — drawer and /cart page share one state.
 *
 * - One source of truth: Shopify's cart. Every change ends with the `sb-cart` section rendered by Shopify and swapped into
 *   every [data-sb-cart] on the page (drawer and /cart), plus the header count.
 * - Instant feedback: quantity, line total, sum and progress move at once (from known unit prices), then match Shopify.
 * - Writes go through one queue, so two scripts never write the cart at the same time.
 * - Changes from anywhere are picked up: Dawn product forms (renderContents), Dawn pubsub, Shopify standard actions,
 *   every completed request to /cart/* seen by the browser (PerformanceObserver, so no fetch/XHR patching and no clash
 *   with other scripts' wrappers), other tabs (BroadcastChannel), returning to the tab, the back button.
 * - Gifts: the „Promocja koszyka” entries say which gift line should exist. This script adds it (property `_gratis`),
 *   removes exactly that line when the threshold is no longer met or the promo ended, and removes any gift line that
 *   Shopify prices above 0 (the discount does not apply), so nobody pays for a „gift”.
 * - Sets (Z1): `size` single jars of the set's product type become one line of the hidden set product (price = one jar in
 *   the set), with the visible property „Skład”; a set line that no longer adds up is taken apart into single jars.
 * - Discount code (DECISIONS 23 Oct): codes never apply to sets. A code typed with a set is tried against the same jars
 *   without the set; the cheaper wins (`_kod_zamiast_zestawu` keeps a winning code so the set is not folded again). A
 *   partner's code that applies to nothing because of the set is kept on the order as the attribute „Kod partnera”.
 * - Checkout guard: the checkout form must post to this shop's /cart; anything that rewrites it is put back.
 * No external code is loaded. Nothing here builds a checkout URL from data.
 */
(() => {
  if (customElements.get('cart-drawer')) return;

  const SECTIONS = ['sb-cart', 'cart-icon-bubble'];
  const OWN_MARK = 'sb=1';
  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const r = () => window.routes || {};
  const cartUrl = () => r().cart_url || '/cart';
  const url = {
    add: () => r().cart_add_url || '/cart/add',
    change: () => r().cart_change_url || '/cart/change',
    update: () => r().cart_update_url || '/cart/update',
  };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'PLN';
  const moneyFmt = new Intl.NumberFormat(document.documentElement.lang || 'pl', { style: 'currency', currency });
  const money = (cents) => moneyFmt.format(cents / 100);
  const store = {
    get: (k) => {
      try {
        return sessionStorage.getItem(k);
      } catch (e) {
        return null;
      }
    },
    set: (k, v) => {
      try {
        sessionStorage.setItem(k, v);
      } catch (e) {}
    },
  };

  // which sets' jar lists are open (by set kind), kept for the tab so a new render or page opens them again
  const openSets = new Set();
  try {
    JSON.parse(sessionStorage.getItem('sb-set-open') || '[]').forEach((k) => openSets.add(k));
  } catch (e) {}
  const saveOpenSets = () => {
    try {
      sessionStorage.setItem('sb-set-open', JSON.stringify([...openSets]));
    } catch (e) {}
  };

  const state = {
    touch: null,
    queue: Promise.resolve(),
    pending: 0,
    lastRefresh: 0,
    refreshTimer: null,
    qtyTimers: new Map(),
    blocked: {},
    bars: new Map(),
    pend: new Map(),
    qtyBase: new Map(),
    engineRunning: false,
    undo: null,
    cel: null,
    toastTimer: null,
    noteTimer: null,
    data: null,
  };

  /* ---------------------------------------------------------------- network */

  async function post(base, body, { sections = true } = {}) {
    const payload = sections ? { ...body, sections: SECTIONS.join(','), sections_url: location.pathname } : body;
    const res = await fetch(`${base}.js?${OWN_MARK}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || 'cart');
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  // a read the shop refused throws, with its status: 3 Nov, at 429 a read that came back empty was taken for an old copy and
  // asked for again at once (6 × cart.js + 6 × ?sections in about 0,5 s)
  function refused(res) {
    const err = new Error(`cart read ${res.status}`);
    err.status = res.status;
    return err;
  }

  // what retryLater waits between engine passes, and which failures are worth waiting for
  const RETRY_MS = [1000, 2000, 4000, 8000];
  const isBusy = (err) => !!err && (err.status === 429 || err.status >= 500);

  async function fetchSections() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const res = await fetch(`${root}?sections=${SECTIONS.join(',')}&${OWN_MARK}&t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) throw refused(res);
    return res.json();
  }

  /* The lock shared by every page of the shop (Web Locks) and the cart read just before a write, for SBSets.step: 2 Nov, real
     cart: a second page of the shop (another tab, or a page the browser prerendered) planned the same swap from its own old
     copy, and 6 mixes came back as 12 at the 8 price. */
  function locked(task) {
    if (navigator.locks && navigator.locks.request) return navigator.locks.request('sb-cart-engine', () => task());
    return task();
  }

  /* A set swap in progress (sb-sets.js `finish`), kept where every page of the shop sees it: a page that goes away between
     the add and the update leaves it, and the next pass finishes the swap. */
  const JOURNAL = 'sb-set-swap';
  const journal = {
    get() {
      try {
        const rec = JSON.parse(localStorage.getItem(JOURNAL) || 'null');
        return rec && Date.now() - rec.at < 864e5 ? rec : null;
      } catch (e) {
        return null;
      }
    },
    set(rec) {
      try {
        localStorage.setItem(JOURNAL, JSON.stringify(rec));
      } catch (e) {}
    },
    clear() {
      try {
        localStorage.removeItem(JOURNAL);
      } catch (e) {}
    },
  };

  async function liveCart() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const res = await fetch(`${root}cart.js?${OWN_MARK}&t=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!res.ok) throw refused(res);
    return res.json();
  }

  function enqueue(task) {
    state.pending += 1;
    setBusy(true);
    const run = state.queue.then(task, task);
    state.queue = run.catch(() => {}).finally(() => {
      state.pending -= 1;
      if (state.pending === 0) setBusy(false);
    });
    return run;
  }

  function announce(response) {
    try {
      state.channel && state.channel.postMessage({ at: Date.now() });
    } catch (e) {}
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sb-cart', cartData: response });
    }
  }

  /* ---------------------------------------------------------------- render */

  function readData(root = document) {
    const el = root.querySelector('[data-sb-cart] [data-sb-cart-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function render(sections, opts = {}) {
    if (!sections) return;
    const html = sections['sb-cart'];
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-sb-cart]');
      const freshData = fresh && fresh.querySelector('[data-sb-cart-data]');
      const current = document.querySelector('[data-sb-cart] [data-sb-cart-data]');
      const same = freshData && current && freshData.textContent === current.textContent && !opts.highlightNew;
      if (fresh && !same) {
        document.querySelectorAll('[data-sb-cart]').forEach((old) => swap(old, fresh.cloneNode(true), opts));
      }
    }
    const bubble = sections['cart-icon-bubble'];
    const icon = document.getElementById('cart-icon-bubble');
    if (bubble && icon) {
      const doc = new DOMParser().parseFromString(bubble, 'text/html');
      const src = doc.querySelector('.shopify-section');
      if (src) icon.innerHTML = src.innerHTML;
    }
    state.data = readData();
    state.lastRefresh = Date.now();
    afterRender();
    if (!opts.fromEngine) enqueue(engine);
  }

  function swap(old, fresh, opts) {
    const oldFill = old.querySelector('[data-sb-fill]');
    const newFill = fresh.querySelector('[data-sb-fill]');
    const target = newFill ? newFill.style.width : null;
    if (oldFill && newFill && !reduceMotion) newFill.style.width = oldFill.style.width;

    const oldKeys = new Set([...old.querySelectorAll('[data-sb-line]')].map((l) => l.dataset.key));
    if (opts.highlightNew || oldKeys.size) {
      fresh.querySelectorAll('[data-sb-line]').forEach((l) => {
        if (!oldKeys.has(l.dataset.key)) l.classList.add('is-new');
      });
    }
    old.querySelectorAll('[data-sb-sw]:not(.is-in)').forEach((sw) => {
      const twin = fresh.querySelector(`[data-sb-sw="${CSS.escape(sw.dataset.sbSw)}"]`);
      if (twin && twin.classList.contains('is-in')) twin.classList.add('is-pop');
    });
    old.querySelectorAll('[data-sb-node]').forEach((n) => {
      const twin = fresh.querySelector(`[data-sb-node="${n.dataset.sbNode}"][data-at="${n.dataset.at}"]`);
      if (twin && twin.classList.contains('is-done') && !n.classList.contains('is-done')) twin.classList.add('is-pop');
    });

    const oldNote = old.querySelector('[data-sb-note]');
    const newNote = fresh.querySelector('[data-sb-note]');
    const noteFocused = oldNote && document.activeElement === oldNote;
    if (oldNote && newNote && (noteFocused || oldNote.dataset.dirty === '1')) {
      newNote.value = oldNote.value;
      newNote.dataset.dirty = oldNote.dataset.dirty || '';
    }
    const oldInvoice = old.querySelector('[data-sb-invoice]');
    const newInvoice = fresh.querySelector('[data-sb-invoice]');
    if (oldInvoice && newInvoice && oldInvoice.dataset.dirty === '1') {
      newInvoice.checked = oldInvoice.checked;
      newInvoice.dataset.dirty = '1';
    }
    const oldCodeBox = old.querySelector('[data-sb-code-box]');
    const newCodeBox = fresh.querySelector('[data-sb-code-box]');
    if (oldCodeBox && newCodeBox && oldCodeBox.open) newCodeBox.setAttribute('open', '');
    const oldCodeInput = old.querySelector('[data-sb-code-input]');
    const newCodeInput = fresh.querySelector('[data-sb-code-input]');
    const codeFocused = oldCodeInput && document.activeElement === oldCodeInput;
    if (oldCodeInput && newCodeInput && oldCodeInput.value) newCodeInput.value = oldCodeInput.value;
    // a set's jar list stays open while its jars change (his 3 Nov: „to drop down window z posypkami w zestawie się zamyka”)
    fresh.querySelectorAll('[data-sb-set-kind] details.sb-line__set').forEach((d) => {
      if (openSets.has(d.closest('[data-sb-set-kind]').dataset.sbSetKind)) d.setAttribute('open', '');
    });
    const oldDetails = old.querySelector('.sb-cart__note');
    const newDetails = fresh.querySelector('.sb-cart__note');
    if (oldDetails && newDetails && !oldDetails.open) newDetails.removeAttribute('open');

    const oldScroll = old.querySelector('[data-sb-scroll]');
    const scrollTop = oldScroll ? oldScroll.scrollTop : 0;
    const focusedKey = document.activeElement && document.activeElement.closest && document.activeElement.closest('[data-sb-line]');
    const focusSel = focusedKey ? focusSelector(document.activeElement) : null;
    const before = reduceMotion ? null : flipMeasure(old);
    // only an add is celebrated (his 3 Nov: „usunąłem, więc nie ma co świętować”): more jars in the cart than before, not lines that
    // are merely new after a removal folded or unfolded a set
    const countOf = (root) => {
      const el = root.querySelector('[data-sb-cart-data]');
      try {
        return el ? Number(JSON.parse(el.textContent).count) || 0 : 0;
      } catch (e) {
        return 0;
      }
    };
    const grew = countOf(fresh) > countOf(old);
    // the count reaches a step (his 3 Nov): the old prices under the jars crumble into the glitter as they go
    const crumble = [];
    if (!reduceMotion) {
      old.querySelectorAll('[data-sb-pool]').forEach((card) => {
        const id = card.dataset.sbSetId;
        const was = card.querySelector('[data-sb-glitter]');
        const twin = fresh.querySelector(`[data-sb-pool][data-sb-set-id="${CSS.escape(id)}"] [data-sb-glitter]`);
        if (!grew || !was || !twin || Number(twin.dataset.tier || 0) <= Number(was.dataset.tier || 0)) return;
        card.querySelectorAll('.sb-pool__price').forEach((p) => {
          if (!p.textContent.trim() || p.textContent.trim() === '…') return;
          const r = p.getBoundingClientRect();
          if (r.width && r.bottom > 0 && r.top < window.innerHeight) crumble.push(r);
        });
      });
    }
    // what the shopper is looking at stays where it is: the set card, else the first block on screen (a line added above it
    // or lines folded into a set must not push it away)
    // (his 3 Nov: „niepotrzebnie wszystko to skacze”): first the thing just touched (the set whose jar was removed, the line whose
    // quantity changed) stays under the finger; /cart scrolls the window, the drawer its own list
    const pageScroll = !!oldScroll && getComputedStyle(oldScroll).overflowY === 'visible';
    const anchorOf = (root) => {
      const sc = root.querySelector('[data-sb-scroll]');
      if (!sc) return null;
      const top = pageScroll ? 0 : sc.getBoundingClientRect().top;
      const bottom = pageScroll ? window.innerHeight : sc.getBoundingClientRect().bottom;
      const onScreen = (el) => {
        const r = el.getBoundingClientRect();
        return r.height && r.bottom > top && r.top < bottom;
      };
      if (state.touch && Date.now() - state.touch.at < 8000) {
        const hit = [...root.querySelectorAll(FLIP_ITEMS)].find((el) => flipKey(el) === state.touch.key);
        if (hit && onScreen(hit)) return hit;
      }
      const card = root.querySelector('[data-sb-pool]');
      if (card && onScreen(card)) return card;
      return [...sc.children].find((el) => el.getBoundingClientRect().bottom > top) || null;
    };
    const oldAnchor = oldScroll && (pageScroll || oldScroll.scrollTop > 0 || (state.touch && Date.now() - state.touch.at < 8000)) ? anchorOf(old) : null;
    const anchorKey = oldAnchor ? flipKey(oldAnchor) : null;
    const anchorTop = oldAnchor ? oldAnchor.getBoundingClientRect().top : 0;
    keepPictures(old, fresh);
    // what leaves for good (the nudge once its goal is reached) fades out where it was instead of vanishing
    const ghosts = [];
    const pops = [];
    if (before) {
      // the nudge once its goal is reached, the set card and its section when the last jar goes
      old.querySelectorAll('.sb-cart__gap, [data-sb-pool], [data-sb-pool-more]').forEach((el) => {
        const sel = el.matches('.sb-cart__gap')
          ? '.sb-cart__gap'
          : el.matches('[data-sb-pool]')
            ? `[data-sb-pool][data-sb-set-id="${CSS.escape(el.dataset.sbSetId)}"]`
            : `[data-sb-pool-more="${CSS.escape(el.dataset.sbPoolMore)}"]`;
        if (fresh.querySelector(sel)) return;
        const r = el.getBoundingClientRect();
        if (!r.height || r.bottom < 0 || r.top > window.innerHeight) return;
        const g = el.cloneNode(true);
        g.setAttribute('aria-hidden', 'true');
        g.style.cssText = `position:fixed;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;margin:0;z-index:2147483001;pointer-events:none`;
        ghosts.push(g);
      });
      // the large set's two places when the cart drops below the first step (his „pękają jak bańki i znikają”): no burst. Placed
      // against the card once the new cart is in (what is above the card can move first), not where they were on screen.
      old.querySelectorAll('[data-sb-pool]').forEach((card) => {
        const twin = fresh.querySelector(`[data-sb-pool][data-sb-set-id="${CSS.escape(card.dataset.sbSetId)}"]`);
        if (!twin) return;
        const left = twin.querySelectorAll('.sb-pool__slot').length;
        const c = card.getBoundingClientRect();
        [...card.querySelectorAll('.sb-pool__slot')].slice(left).forEach((el) => {
          const r = el.getBoundingClientRect();
          if (!r.height) return;
          const g = el.cloneNode(true);
          g.setAttribute('aria-hidden', 'true');
          g.dataset.sbPop = '';
          g.style.cssText = `position:fixed;width:${r.width}px;height:${r.height}px;margin:0;list-style:none;display:grid;justify-items:center;gap:4px;z-index:2147483001;pointer-events:none`;
          pops.push({ g, twin, dx: r.left - c.left });
        });
      });
    }
    const shipWasDone = !!old.querySelector('[data-sb-node="ship"].is-done');
    old.replaceWith(fresh);
    ghosts.forEach((g) => {
      document.body.appendChild(g);
      g.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.96)' }], { duration: 320, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }).finished.then(() => g.remove(), () => g.remove());
    });

    const newScroll = fresh.querySelector('[data-sb-scroll]');
    if (newScroll) newScroll.scrollTop = scrollTop;
    if (newScroll && anchorKey) {
      const again = [...fresh.querySelectorAll(FLIP_ITEMS)].find((el) => flipKey(el) === anchorKey);
      if (again) {
        const shift = again.getBoundingClientRect().top - anchorTop;
        if (pageScroll) window.scrollBy(0, shift);
        else newScroll.scrollTop += shift;
      }
    }
    if (before) {
      pops.forEach(({ g, twin, dx }) => {
        // on the row of places as it is now (the card's insides can shift while the cart changes), where they stood along it
        const c = twin.getBoundingClientRect();
        const row = twin.querySelector('.sb-pool__slot');
        const top = row ? row.getBoundingClientRect().top : c.top;
        if (top > window.innerHeight || top + 60 < 0) return;
        g.style.left = `${c.left + dx}px`;
        g.style.top = `${top}px`;
        document.body.appendChild(g);
        g.animate(
          [
            { opacity: 1, transform: 'none' },
            { opacity: 1, transform: 'scale(1.16)', offset: 0.4 },
            { opacity: 0, transform: 'scale(0.5)' },
          ],
          { duration: 280, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }
        ).finished.then(() => g.remove(), () => g.remove());
      });
      flipPlay(before, fresh, grew);
      // free shipping reached by an add: a burst on the truck
      const shipNode = fresh.querySelector('[data-sb-node="ship"].is-done');
      if (grew && shipNode && !shipWasDone && window.SBBurst) setTimeout(() => window.SBBurst.on(shipNode, { scale: 0.9 }), 450);
      if (window.SBBurst) crumble.forEach((r, i) => setTimeout(() => window.SBBurst.at(r.left + r.width / 2, r.top + r.height / 2, { scale: 0.3 }), i * 35));
      // a new line in the cart: a glitter burst where its picture lands (sb-burst.js), as it finishes sliding in
      if (window.SBBurst && grew) {
        fresh.querySelectorAll('[data-sb-line].is-new').forEach((line) =>
          setTimeout(() => window.SBBurst.on(line.querySelector('img') || line, { scale: 1 }), 300)
        );
      }
    }
    if (noteFocused && newNote) {
      newNote.focus();
      newNote.setSelectionRange(newNote.value.length, newNote.value.length);
    }
    if (codeFocused && newCodeInput) newCodeInput.focus();
    if (focusSel) {
      const again = fresh.querySelector(focusSel);
      if (again && !again.disabled) again.focus();
    }
    if (newFill && target && !reduceMotion) requestAnimationFrame(() => requestAnimationFrame(() => (newFill.style.width = target)));
  }

  /* Smooth swaps (his 3 Nov: „jak z propozycji dodaję produkty, to nagle wszystko przeskakuje i przeładowuje się”). The cart is
     still Shopify's section swapped in whole, but what stays slides from where it was (FLIP), what is new comes in, a place that
     has just been filled pops, and pictures and the glitter canvas already on screen are carried over, so nothing blinks or
     starts again from empty. Only what is on screen moves. Off with reduced motion. */
  const FLIP_ITEMS = '[data-sb-scroll] > *, [data-sb-line], [data-sb-pool], [data-sb-pool-more], .sb-set, .sb-cart__gap, [data-sb-sw], .sb-pool__slot, .sb-suggest';
  const FLIP_EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
  function flipKey(el) {
    if (el.matches('[data-sb-line][data-sb-set-kind]')) return `ls:${el.dataset.sbSetKind}`;
    if (el.matches('[data-sb-line]')) return `l:${el.dataset.key}`;
    if (el.matches('[data-sb-sw]')) return `s:${el.dataset.sbSw}`;
    if (el.matches('.sb-suggest')) {
      const add = el.querySelector('[data-sb-add]');
      return `g:${add ? add.dataset.sbAdd : ''}`;
    }
    if (el.matches('.sb-pool__slot')) {
      const card = el.closest('[data-sb-pool]');
      return `p:${card ? card.dataset.sbSetId : ''}:${[...el.parentElement.children].indexOf(el)}`;
    }
    if (el.matches('[data-sb-pool]')) return `c:${el.dataset.sbSetId}`;
    if (el.matches('[data-sb-pool-more]')) return `m:${el.dataset.sbPoolMore}`;
    return `b:${String(el.className).split(' ')[0]}`;
  }
  function flipMeasure(root) {
    const scroller = root.querySelector('[data-sb-scroll]');
    if (!scroller || !scroller.offsetParent) return null;
    const map = new Map();
    root.querySelectorAll(FLIP_ITEMS).forEach((el) => {
      const key = flipKey(el);
      if (!map.has(key)) map.set(key, { r: el.getBoundingClientRect(), filled: el.classList.contains('is-in') });
    });
    return map;
  }
  function flipPlay(before, root, grew) {
    const vh = window.innerHeight;
    const seen = new Set();
    const els = [...root.querySelectorAll(FLIP_ITEMS)];
    const now = new Map(els.map((el) => [el, el.getBoundingClientRect()]));
    els.forEach((el) => {
      const key = flipKey(el);
      if (seen.has(key)) return;
      seen.add(key);
      const r = now.get(el);
      if (r.bottom < 0 || r.top > vh) return;
      const was = before.get(key);
      // the nearest tracked parent: its own move is taken off, or the element would move twice
      const parent = el.parentElement && el.parentElement.closest(FLIP_ITEMS);
      const pWas = parent && before.get(flipKey(parent));
      const pNow = parent && now.get(parent);
      if (was) {
        let dx = was.r.left - r.left;
        let dy = was.r.top - r.top;
        if (pWas && pNow) {
          dx -= pWas.r.left - pNow.left;
          dy -= pWas.r.top - pNow.top;
        }
        // a place that narrows or widens (six to a row ↔ eight) changes its size with its move
        const sx = el.matches('.sb-pool__slot') && r.width ? was.r.width / r.width : 1;
        const sy = el.matches('.sb-pool__slot') && r.height ? was.r.height / r.height : 1;
        if (Math.abs(sx - 1) > 0.02 || Math.abs(sy - 1) > 0.02) {
          el.animate(
            [
              { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, transformOrigin: '0 0' },
              { transform: 'none', transformOrigin: '0 0' },
            ],
            { duration: 380, easing: FLIP_EASE }
          );
        } else if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
          el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 320, easing: FLIP_EASE });
        }
        if (el.matches('.sb-pool__slot') && el.classList.contains('is-in') && !was.filled) {
          // his 3 Nov: a little slower, and a small glitter burst when the jar lands in its place
          const box = el.querySelector('.sb-pool__box');
          if (box) box.animate([{ opacity: 0, transform: 'scale(0.96)' }, { opacity: 1, transform: 'none' }], { duration: 500, easing: FLIP_EASE });
          if (window.SBBurst && grew) setTimeout(() => window.SBBurst.on(box, { scale: 0.55 }), 260);
        }
        return;
      }
      // new: lines have their own entrance (.is-new); anything inside a parent that is itself new comes in with it
      if (el.matches('[data-sb-line]') || (parent && !pWas)) return;
      if (el.matches('.sb-pool__slot')) {
        // his 3 Nov: the two places of the large set „wyskakują jak bańki” once the six have moved up
        el.animate(
          [
            { opacity: 0, transform: 'scale(0.3)' },
            { opacity: 1, transform: 'scale(1.14)', offset: 0.6 },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 460, delay: 300 + ([...el.parentElement.children].indexOf(el) % 2) * 70, easing: 'ease-out', fill: 'backwards' }
        );
        const box = el.querySelector('.sb-pool__box');
        if (box && grew && window.SBBurst) setTimeout(() => window.SBBurst.on(box, { scale: 0.4 }), 520);
        return;
      }
      el.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 300, easing: FLIP_EASE });
    });
  }
  // the pictures and the glitter canvas already on screen go into the new cart, so they neither reload nor redraw from empty
  function keepPictures(old, fresh) {
    const byUrl = new Map();
    old.querySelectorAll('img').forEach((img) => {
      const k = img.getAttribute('src');
      if (!k || !img.complete) return;
      if (!byUrl.has(k)) byUrl.set(k, []);
      byUrl.get(k).push(img);
    });
    fresh.querySelectorAll('img').forEach((img) => {
      const list = byUrl.get(img.getAttribute('src'));
      if (!list || !list.length) return;
      const keep = list.shift();
      keep.className = img.className;
      keep.alt = img.alt;
      img.replaceWith(keep);
    });
    old.querySelectorAll('canvas[data-sb-glitter]').forEach((c) => {
      const twin = fresh.querySelector(`canvas[data-sb-glitter="${CSS.escape(c.dataset.sbGlitter)}"]`);
      if (!twin) return;
      ['data-n', 'data-top', 'data-tier', 'aria-label'].forEach((a) => c.setAttribute(a, twin.getAttribute(a) || ''));
      twin.replaceWith(c);
    });
  }

  function focusSelector(el) {
    const line = el.closest('[data-sb-line]');
    if (!line) return null;
    const key = CSS.escape(line.dataset.key);
    for (const attr of ['data-sb-inc', 'data-sb-dec', 'data-sb-remove']) {
      if (el.hasAttribute(attr)) return `[data-sb-line][data-key="${key}"] [${attr}]`;
    }
    return null;
  }

  function afterRender() {
    document.querySelectorAll('[data-sb-set-kind] details.sb-line__set').forEach((d) => {
      if (openSets.has(d.closest('[data-sb-set-kind]').dataset.sbSetKind) && !d.open) d.setAttribute('open', '');
    });
    restoreTerms();
    guardForms();
    keepCodeMsg();
    tray();
    glitter();
  }

  /* The set bar on every page (his „licznik … w tle dla każdego w każdym miejscu”; his card version 2 Nov): how many jars count
     and the cart with its true sum. No price per jar and no price still to come (the cart card shows what each jar costs).
     While the engine is still pricing the cart the sum shows „…”, here and on the checkout button. Not on /cart. Under every
     page with jars of a set (an empty mark in price.liquid) it shows from 0. No set price next to product prices (his call 3 Nov,
     Omnibus): the set price is in the cart's set card only. One bar: the set with most jars.
     Off since 3 Nov (his „usuń całkiem ten bar na dole na razie”): it shows only with the theme setting sb_set_tray. */
  function tray() {
    const d = state.data;
    const drawerEl = document.querySelector('cart-drawer[data-sb-drawer]');
    const enabled = !!drawerEl && drawerEl.dataset.setTray === 'true';
    const defs = ((d && d.sets) || []).filter((x) => x.mode === 'steps');
    const sets = window.SBSets;
    let bar = document.querySelector('[data-sb-tray]');
    const marks = [...document.querySelectorAll('[data-sb-pool-of]')];
    const pools = defs.map((def) => ({ def, n: d.lines.filter((l) => l.set === def.id || l.elig === def.id).reduce((sum, l) => sum + l.qty, 0) }));
    const pending = !!sets && defs.some((def) => sets.plan(d, def));
    const s = d ? d.strings : {};
    document.querySelectorAll('[data-sb-cta]').forEach((cta) => {
      const main = cta.querySelector('[data-sb-cta-main]') || cta;
      if (pending && s.checkout) main.textContent = s.checkout.replace('__TOTAL__', '…');
    });
    const onPage = new Set(marks.map((m) => m.dataset.sbPoolOf));
    const pool = pools.filter((p) => p.n > 0 || onPage.has(p.def.id)).sort((a, b) => b.n - a.n)[0];
    const isCart = document.querySelector('cart-drawer[data-sb-page]');
    if (!enabled || !pool || !sets || isCart) {
      if (bar) bar.hidden = true;
      document.body.classList.remove('sb-tray-on');
      return;
    }
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'sb-tray';
      bar.setAttribute('data-sb-tray', '');
      bar.setAttribute('role', 'region');
      document.body.appendChild(bar);
      // the room the page keeps for it: its height whenever it is shown (CSS may keep it hidden while the product's own
      // „Dodaj do koszyka” is on screen, and it turns compact above the floating one)
      new ResizeObserver(() => {
        if (bar.offsetHeight) document.documentElement.style.setProperty('--sb-tray-h', `${bar.offsetHeight}px`);
      }).observe(bar);
    }
    const label = s.trayCount.replace('__NAME__', pool.def.name || '').replace('__COUNT__', pool.n);
    bar.setAttribute('aria-label', label);
    bar.dataset.sbSetId = pool.def.id;
    bar.classList.toggle('is-on', !!sets.shape(pool.n, pool.def).tier);
    const total = pending ? '…' : money(d.total);
    bar.innerHTML = `<div class="sb-tray__row">
        <span class="sb-tray__count">${escapeHtml(label)}</span>
        ${d.count ? `<button type="button" class="sb-tray__cart" data-sb-tray-cart>${escapeHtml(s.trayCart.replace('__TOTAL__', total))}</button>` : ''}
      </div>`;
    bar.hidden = false;
    document.body.classList.add('sb-tray-on');
    if (bar.offsetHeight) document.documentElement.style.setProperty('--sb-tray-h', `${bar.offsetHeight}px`);
  }

  // the bar's drawing lives in sb-glitter-bar.js (loaded before this file), shared with the collection's set section
  const GlitterBar = window.SBGlitterBar || null;

  function glitter() {
    const c = state.cel;
    state.cel = null;
    const seen = new Set();
    // only the cart's own bars (another bar on the page, e.g. the collection's set section, draws itself)
    document.querySelectorAll('[data-sb-cart] [data-sb-glitter]').forEach((canvas) => {
      const id = canvas.dataset.sbGlitter;
      seen.add(id);
      if (!GlitterBar) return;
      let g = state.bars.get(id);
      if (!g) state.bars.set(id, (g = new GlitterBar(id)));
      // his 3 Nov: the card has just come back (the first jar after an empty cart): its bar fills from empty, it does not jump
      if (g.gone) g.reset();
      g.attach(canvas);
      g.set(Number(canvas.dataset.n), Number(canvas.dataset.top), !!(c && c.id === id));
    });
    state.bars.forEach((g, id) => {
      if (!seen.has(id) && !g.gone) g.forget();
    });
    document.querySelectorAll('[data-sb-pool]').forEach((card) => {
      const id = card.dataset.sbSetId;
      const now = card.classList.contains('is-pending');
      // his 3 Nov: at 6 and at 8 „efekt zaklęcia” on the bar, and the new, lower prices come in with it
      const stepUp = !!(c && c.id === id) && !reduceMotion;
      if (state.pend.get(id) && !now && !reduceMotion) {
        card.querySelectorAll('.sb-pool__price:not(:empty)').forEach((p, i) => {
          p.animate(
            stepUp
              ? [
                  { opacity: 0, transform: 'translateY(6px) scale(0.85)', filter: 'blur(3px)' },
                  { opacity: 1, transform: 'none', filter: 'blur(0)' },
                ]
              : [
                  { opacity: 0, transform: 'translateY(4px)', filter: 'blur(2px)' },
                  { opacity: 1, transform: 'none', filter: 'blur(0)' },
                ],
            stepUp
              ? { duration: 420, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', delay: 260 + i * 55, fill: 'backwards' }
              : { duration: 220, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', delay: i * 28, fill: 'backwards' }
          );
          if (stepUp && window.SBBurst) setTimeout(() => window.SBBurst.on(p, { scale: 0.3 }), 260 + i * 55);
        });
      }
      if (stepUp && window.SBBurst) {
        // the step's sparkle when the bar's fill reaches it, together with its token (review 3 Nov: token and bar move as one)
        const bar = card.querySelector('[data-sb-glitter]');
        const g = state.bars.get(id);
        const top = bar ? Number(bar.dataset.top) || 1 : 1;
        const wait = g ? g.delayTo(Math.min(c.tier, top) / top) : 0;
        setTimeout(() => {
          if (!bar || !bar.isConnected) return;
          const b = bar.getBoundingClientRect();
          const x = b.left + (b.width * Math.min(c.tier, top)) / top;
          window.SBBurst.at(Math.min(b.right, x), b.top + b.height / 2, { scale: 1.6 });
          setTimeout(() => window.SBBurst.at(b.left + b.width * 0.35, b.top + b.height / 2, { scale: 0.9 }), 120);
        }, wait);
      }
      state.pend.set(id, now);
    });
    if (c) {
      try {
        if (navigator.vibrate) navigator.vibrate(18);
      } catch (e) {}
    }
  }

  function setBusy(busy) {
    document.querySelectorAll('[data-sb-cart]').forEach((c) => c.classList.toggle('is-busy', busy));
  }

  /* ------------------------------------------------- instant (optimistic) UI */

  function optimistic() {
    const d = state.data;
    if (!d) return;
    let qualifying = 0;
    let count = 0;
    document.querySelectorAll('[data-sb-cart]').forEach((cart, i) => {
      let q = 0;
      let c = 0;
      cart.querySelectorAll('[data-sb-line]').forEach((line) => {
        const qty = Number(line.dataset.qty);
        c += qty;
        if (!line.dataset.gift) q += Number(line.dataset.unit) * qty;
      });
      if (i === 0) {
        qualifying = q;
        count = c;
      }
      const goals = [{ kind: 'ship', at: d.shipAt, label: d.strings.goalShip }].concat(
        (d.gifts || []).map((g) => ({ kind: 'gift', at: g.at, label: g.label, handle: g.handle }))
      );
      const next = goals.filter((g) => q < g.at).sort((a, b) => a.at - b.at)[0];
      const giftFree = (h) => d.lines.some((l) => l.gift === h && l.line === 0);
      const done = goals.filter((g) => q >= g.at && (g.kind !== 'gift' || giftFree(g.handle)));
      const msg = cart.querySelector('[data-sb-progress-msg]');
      if (msg) {
        if (next) {
          const pre = done.length ? (done.some((g) => g.kind === 'gift') ? d.strings.doneGift : d.strings.doneShip) + ' ' : '';
          msg.innerHTML = pre + d.strings.left.replace('__GOAL__', escapeHtml(next.label)).replace('__AMOUNT__', money(next.at - q));
        } else {
          msg.textContent = (d.gifts || []).length ? d.strings.allBoth : d.strings.allShip;
        }
      }
      const fill = cart.querySelector('[data-sb-fill]');
      if (fill) fill.style.width = `${Math.min(100, (q * 100) / Math.max(d.maxAt, 1))}%`;
      cart.querySelectorAll('[data-sb-node]').forEach((n) => {
        const reached = q >= Number(n.dataset.at);
        if (reached && !n.classList.contains('is-done')) n.classList.add('is-pop');
        n.classList.toggle('is-done', reached);
      });
      const cta = cart.querySelector('[data-sb-cta]');
      if (cta) (cta.querySelector('[data-sb-cta-main]') || cta).textContent = d.strings.checkout.replace('__TOTAL__', money(q));
      const countEl = cart.querySelector('.sb-cart__count');
      if (countEl) countEl.textContent = `(${c})`;
    });
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
    if (bubble && count < 100) bubble.textContent = count;
    return qualifying;
  }

  /* A line's properties from data-sb-props. Liquid's `json` gives them as [[name, value], …] (not an object) with the values
     HTML-escaped (3 Nov, real cart: „Usuń” next to a jar of a set found no set, and „Cofnij” brought a set back without its
     properties, because both read the pairs as an object). */
  function lineProps(line) {
    let raw;
    try {
      raw = JSON.parse(line.dataset.sbProps || '{}');
    } catch (e) {
      return {};
    }
    const pairs = Array.isArray(raw) ? raw.filter((p) => Array.isArray(p) && p.length === 2) : Object.entries(raw || {});
    const un = (window.SBSets && window.SBSets.unescapeHtml) || ((x) => x);
    return Object.fromEntries(pairs.map(([k, v]) => [k, typeof v === 'string' ? un(v) : v]));
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  }

  function setLineQty(line, qty) {
    line.dataset.qty = qty;
    const out = line.querySelector('[data-sb-qty]');
    if (out) out.textContent = qty;
    const price = line.querySelector('[data-sb-line-price]');
    if (price) price.innerHTML = `<span>${money(Number(line.dataset.unit) * qty)}</span>`;
    const dec = line.querySelector('[data-sb-dec]');
    if (dec) dec.disabled = qty <= 1;
    const inc = line.querySelector('[data-sb-inc]');
    const max = line.dataset.max ? Number(line.dataset.max) : Infinity;
    if (inc) inc.disabled = qty >= max;
  }

  function lineError(line, text) {
    const el = line && line.querySelector('[data-sb-line-error]');
    if (!el) return;
    el.textContent = text;
    el.hidden = !text;
  }

  /* ---------------------------------------------------------------- actions */

  /* The shopper's +/− as a change, applied under the shared lock to the line as it is in the cart now: another page may
     have changed it meanwhile, or the engine may have taken it into a set (then nothing is written and the cart shows what
     it holds). */
  function sendQty(key, delta, max) {
    return enqueue(() =>
      locked(async () => {
        const cart = await liveCart();
        const live = cart && cart.items.find((i) => i.key === key);
        if (!live) {
          const sections = await fetchSections().catch(() => null);
          if (sections) render(sections);
          return;
        }
        const quantity = Math.min(max, Math.max(1, live.quantity + delta));
        if (quantity === live.quantity) return;
        const res = await post(url.change(), { id: key, quantity });
        render(res.sections);
        announce(res);
      }).catch((err) => failed(err, key))
    );
  }

  function changeQty(line, delta) {
    const key = line.dataset.key;
    const max = line.dataset.max ? Number(line.dataset.max) : Infinity;
    if (!state.qtyBase.has(key)) state.qtyBase.set(key, Number(line.dataset.qty));
    let qty = Number(line.dataset.qty) + delta;
    if (qty < 1) qty = 1;
    if (qty > max) {
      qty = max;
      lineError(line, state.data ? state.data.strings.maxStock.replace('__COUNT__', max) : '');
    } else {
      lineError(line, '');
    }
    document.querySelectorAll(`[data-sb-line][data-key="${CSS.escape(key)}"]`).forEach((l) => setLineQty(l, qty));
    optimistic();
    clearTimeout(state.qtyTimers.get(key));
    setBusy(true);
    state.qtyTimers.set(
      key,
      setTimeout(() => {
        state.qtyTimers.delete(key);
        const base = state.qtyBase.get(key);
        state.qtyBase.delete(key);
        sendQty(key, qty - base, max);
      }, 250)
    );
  }

  function removeLine(line) {
    const key = line.dataset.key;
    let group = [];
    try {
      group = JSON.parse(line.dataset.sbGroup || '[]');
    } catch (e) {}
    if (group.length) {
      // a whole set at once (his „usunąć cały zestaw naraz”), with „Cofnij”: undo puts its jars back and the cart makes
      // the set again from them
      const name = ((line.querySelector('.sb-line__name') || {}).textContent || '').trim();
      const jars = ((state.data && state.data.lines) || []).filter((l) => group.includes(l.key)).flatMap((l) => l.comps);
      line.classList.add('is-leaving');
      enqueue(() =>
        locked(() => post(url.update(), { updates: Object.fromEntries(group.map((k) => [k, 0])) }))
          .then((res) => {
            render(res.sections);
            announce(res);
            if (jars.length) {
              state.undo = groupJars(jars);
              toast(state.data ? state.data.strings.removed.replace('__PRODUCT__', name) : name);
            }
          })
          .catch((err) => failed(err, key))
      );
      return;
    }
    const d = state.data && state.data.lines.find((l) => l.key === key);
    const name = (line.querySelector('.sb-line__name') || {}).textContent || '';
    document.querySelectorAll(`[data-sb-line][data-key="${CSS.escape(key)}"]`).forEach((l) => {
      l.classList.add('is-leaving');
      l.dataset.qty = 0;
    });
    optimistic();
    enqueue(() =>
      locked(() => post(url.change(), { id: key, quantity: 0 }))
        .then((res) => {
          render(res.sections);
          announce(res);
          if (d) {
            state.undo = { id: d.variant, quantity: d.qty };
            if (line.dataset.sbProps) state.undo.properties = { ...lineProps(line), _nr: Date.now().toString(36) };
            toast(state.data ? state.data.strings.removed.replace('__PRODUCT__', name.trim()) : name.trim());
          }
        })
        .catch((err) => failed(err, key))
    );
  }

  function addVariant(button) {
    const id = Number(button.dataset.sbAdd);
    if (!id) return;
    button.disabled = true;
    button.classList.add('is-loading');
    enqueue(() =>
      locked(() => post(url.add(), { items: [{ id, quantity: 1, properties: { _sb: button.dataset.sbSource || 'koszyk' } }] }))
        .then((res) => {
          render(res.sections, { highlightNew: true });
          announce(res);
        })
        .catch((err) => failed(err, null, 'add'))
    );
  }

  function failed(err, key, kind) {
    console.warn('[sb-cart]', err);
    const line = key ? document.querySelector(`[data-sb-line][data-key="${CSS.escape(key)}"]`) : null;
    refresh().then(() => {
      const again = key ? document.querySelector(`[data-sb-line][data-key="${CSS.escape(key)}"]`) : null;
      const s = (state.data && state.data.strings) || {};
      const busy = isBusy(err) && (kind === 'add' ? s.busyAdd : s.busyChange);
      const text = busy || (err && err.data && err.data.description) || s.error || '';
      if (again) lineError(again, text);
      else if (!line) toast(text, false);
    });
  }

  function toast(text, withUndo = true) {
    const t = document.querySelector('[data-sb-toast]');
    if (!t) return;
    t.querySelector('[data-sb-toast-text]').textContent = text;
    t.querySelector('[data-sb-undo]').hidden = !withUndo;
    t.hidden = false;
    requestAnimationFrame(() => t.classList.add('is-on'));
    clearTimeout(state.toastTimer);
    state.toastTimer = setTimeout(hideToast, 5000);
  }

  function hideToast() {
    const t = document.querySelector('[data-sb-toast]');
    if (!t) return;
    t.classList.remove('is-on');
    setTimeout(() => (t.hidden = true), 250);
  }

  /* ---------------------------------------------------------------- discount code */

  /* DECISIONS 23 Oct („Sets: partner codes do not apply; the set must beat the code”, „A better code beats the set”):
     codes never apply to a set line. A code typed while the cart holds a set is tried both ways — with the set (the code
     only reaches other lines) and with the same jars as single lines — and Shopify's own totals decide; the cheaper stays.
     A partner's code that ends up applying to nothing is taken off the cart and kept as the attribute „Kod partnera”, so
     the order still shows who referred it. */
  const sameCode = (a, b) => String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
  const isPartnerCode = (code) =>
    String((state.data && state.data.partnerCodes) || '')
      .split(/[,\n]+/)
      .filter(Boolean)
      .some((c) => sameCode(c, code));
  const jarsOf = (l) => (l.comps.length === l.qty ? l.comps : Array.from({ length: l.qty }, () => l.comps).flat());

  /* The answer to a typed code stays on screen for a while, also through the re-render after a set is folded back. */
  function codeMsg(text) {
    state.codeMsg = text ? { text, at: Date.now() } : null;
    document.querySelectorAll('[data-sb-code-msg]').forEach((m) => (m.textContent = text || ''));
  }

  function keepCodeMsg() {
    const m = state.codeMsg;
    if (!m || Date.now() - m.at > 12000) return;
    document.querySelectorAll('[data-sb-code-msg]').forEach((el) => {
      if (!el.textContent.trim()) el.textContent = m.text;
    });
  }

  function codeBusy(busy) {
    document.querySelectorAll('.sb-cart__code-apply').forEach((b) => b.setAttribute('aria-busy', String(busy)));
  }

  async function cartJson() {
    const res = await fetch(`${cartUrl()}.js?${OWN_MARK}`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart');
    return res.json();
  }

  function applyCode(raw) {
    const code = String(raw || '').trim();
    if (!code) return;
    const strings = (state.data && state.data.strings) || {};
    codeMsg(strings.codeChecking);
    codeBusy(true);
    enqueue(() =>
      locked(async () => {
        let text = '';
        try {
          const before = await cartJson();
          const kept = (before.discount_codes || []).filter((c) => c.applicable && !sameCode(c.code, code)).map((c) => c.code);
          const withSet = await post(url.update(), { discount: [...kept, code].join(',') }, { sections: false });
          const entryWithSet = (withSet.discount_codes || []).find((c) => sameCode(c.code, code));
          const setLines = ((state.data && state.data.lines) || []).filter((l) => l.set && withSet.items.some((i) => i.key === l.key));
          let final = withSet;
          let wins = false;
          if (setLines.length) {
            // The same jars without the set, with the code: does the code save more?
            await post(url.update(), { attributes: { _kod_zamiast_zestawu: code } }, { sections: false });
            const items = groupJars(setLines.flatMap(jarsOf));
            if (items.length) await post(url.add(), { items }, { sections: false });
            const single = await post(url.update(), { updates: Object.fromEntries(setLines.map((l) => [l.key, 0])) }, { sections: false });
            const entrySingle = (single.discount_codes || []).find((c) => sameCode(c.code, code));
            wins = Boolean(entrySingle && entrySingle.applicable && single.total_price < withSet.total_price);
            if (!wins) {
              // The set is cheaper: back to the set (the engine folds the jars again after the render below).
              final = await post(url.update(), { attributes: { _kod_zamiast_zestawu: '' } }, { sections: false });
            } else {
              final = single;
            }
          }
          let attrs = null;
          if (wins) {
            text = (strings.codeWins || '').replace('__CODE__', code.toUpperCase());
          } else if (entryWithSet && entryWithSet.applicable) {
            text = (strings.codeApplied || '').replace('__CODE__', code.toUpperCase());
            if (setLines.length) text = `${text} ${strings.codeNotOnSets || ''}`;
          } else {
            // The code applies to nothing: take it off (no failed code at checkout); keep a partner's code for the referral.
            if (setLines.length) {
              text = strings.codeNotOnSets || '';
              if (isPartnerCode(code)) {
                attrs = { 'Kod partnera': code.toUpperCase() };
                text = `${text} ${strings.codeReferral || ''}`;
              }
            } else {
              text = strings.codeInvalid || '';
            }
            final = await post(url.update(), { discount: kept.join(','), ...(attrs ? { attributes: attrs } : {}) }, { sections: false });
          }
          const res = await post(url.update(), {}, { sections: true });
          render(res.sections);
          announce(res);
        } catch (err) {
          console.warn('[sb-cart] code', err);
          text = strings.codeError || '';
          enqueue(refresh);
        } finally {
          codeBusy(false);
          codeMsg(text);
          document.querySelectorAll('[data-sb-code-input]').forEach((i) => (i.value = ''));
        }
      })
    );
  }

  function removeCode(code) {
    enqueue(() =>
      locked(async () => {
        try {
          const before = await cartJson();
          const rest = (before.discount_codes || []).filter((c) => !sameCode(c.code, code)).map((c) => c.code);
          const body = { discount: rest.join(',') };
          if (state.data && state.data.codeOverSet && sameCode(state.data.codeOverSet, code)) body.attributes = { _kod_zamiast_zestawu: '' };
          const res = await post(url.update(), body);
          render(res.sections);
          announce(res);
        } catch (err) {
          console.warn('[sb-cart] code', err);
          enqueue(refresh);
        }
      })
    );
  }

  /* ---------------------------------------------------------------- gifts */

  async function engine() {
    // a page the browser prerendered never writes; it runs once the shopper opens it
    if (state.engineRunning || !state.data || document.prerendering) return;
    state.engineRunning = true;
    try {
      for (let i = 0; i < 6; i += 1) {
        const d = state.data;
        if (!d) break;
        const q = d.qualifying;
        const giftLines = d.lines.filter((l) => l.gift);
        giftLines.filter((l) => l.line > 0).forEach((l) => (state.blocked[l.gift] = q));
        const want = new Set(
          (d.gifts || [])
            .filter((g) => g.available && g.variant && q >= g.at && state.blocked[g.handle] !== q)
            .map((g) => g.handle)
        );
        const seen = new Set();
        const updates = {};
        giftLines.forEach((l) => {
          if (!want.has(l.gift) || l.line > 0 || seen.has(l.gift)) updates[l.key] = 0;
          else if (l.qty > 1) updates[l.key] = 1;
          seen.add(l.gift);
        });
        if (Object.keys(updates).length) {
          const res = await post(url.update(), { updates });
          render(res.sections, { fromEngine: true });
          announce(res);
          continue;
        }
        const missing = (d.gifts || []).filter((g) => want.has(g.handle) && !seen.has(g.handle));
        if (missing.length) {
          const res = await post(url.add(), {
            items: missing.map((g) => ({ id: g.variant, quantity: 1, properties: { _gratis: g.handle } })),
          });
          render(res.sections, { fromEngine: true, highlightNew: true });
          announce(res);
          continue;
        }
        if (await sets(d)) continue;
        break;
      }
      state.retry = 0;
    } catch (err) {
      console.warn('[sb-cart] engine', err);
      retryLater(err);
    } finally {
      state.engineRunning = false;
    }
  }

  /* The shop says „too many requests” (429) or fails (5xx): the next pass waits 1, 2, 4, then 8 s (plus a little at random, so
     two pages do not ask together), the cart says it will catch up, and after the fourth wait it stops and asks for a reload.
     Any pass that ends well starts the count again. */
  function retryLater(err) {
    if (!isBusy(err)) return;
    const s = (state.data && state.data.strings) || {};
    const wait = RETRY_MS[state.retry || 0];
    clearTimeout(state.retryTimer);
    if (wait === undefined) {
      state.retry = 0;
      if (s.busyStop) toast(s.busyStop, false);
      return;
    }
    if (!state.retry && s.busyWait) toast(s.busyWait, false);
    state.retry = (state.retry || 0) + 1;
    state.retryTimer = setTimeout(() => enqueue(engine), wait + Math.round(Math.random() * 300));
  }

  /* ---------------------------------------------------------------- sets (Z1) */

  /* DECISIONS 23 Oct „The set is a real product (Z1)”: the set is a hidden product whose price is one jar in the set. When
     the cart holds `size` single jars of the set's product type, they become one set line: quantity = jars, visible „Skład”,
     hidden `_zestaw` (set id), `_sklad` („handle:variant” per jar), `_osobno` (their own prices, for the „osobno” line) and
     `_nr` (keeps two sets from merging into one line). A set line whose quantity no longer matches its Skład, or is not a
     whole number of sets, is taken apart into single jars at their own prices, and the next pass forms sets again from what
     is there. The set line is added before the single jars are removed, and a failed removal takes it out again, so a
     failure never charges a jar twice. */
  const groupJars = (comps) => {
    const back = new Map();
    comps.forEach((c) => back.set(c.v, (back.get(c.v) || 0) + 1));
    return [...back].filter(([v]) => v > 0).map(([v, q]) => ({ id: v, quantity: q, properties: { _sb: 'zestaw' } }));
  };

  /* A fixed set (his call 23 Oct: „zestaw Vibrant”): one of each listed product. The set line's quantity is the number of
     sets and its price is the whole set; `_sklad` lists the products once. */
  async function fixedSet(d, def) {
    const want = def.members || [];
    if (!want.length) return false;
    const setLines = d.lines.filter((l) => l.set === def.id);
    const matches = (comps) => comps.length === want.length && want.every((m) => comps.some((c) => c.h === m.h));
    const broken = setLines.filter((l) => !matches(l.comps));
    if (broken.length) {
      const items = groupJars(broken.filter((l) => l.comps.length).flatMap((l) => Array.from({ length: l.qty }, () => l.comps).flat()));
      if (items.length) await post(url.add(), { items }, { sections: false });
      const res = await post(url.update(), { updates: Object.fromEntries(broken.map((l) => [l.key, 0])) });
      render(res.sections, { fromEngine: true });
      announce(res);
      return true;
    }
    const singles = d.lines.filter((l) => l.elig === def.id).reverse(); // oldest first
    const units = (h) => singles.filter((l) => l.handle === h).reduce((sum, l) => sum + l.qty, 0);
    const k = Math.min(...want.map((m) => units(m.h)));
    if (!(k >= 1)) return false;
    const updates = {};
    want.forEach((m) => {
      let left = k;
      for (const l of singles.filter((x) => x.handle === m.h)) {
        if (!left) break;
        const n = Math.min(l.qty, left);
        updates[l.key] = (updates[l.key] === undefined ? l.qty : updates[l.key]) - n;
        left -= n;
      }
    });
    const regular = want.reduce((sum, m) => {
      const line = singles.find((l) => l.handle === m.h);
      return sum + (line ? line.regular : m.p);
    }, 0);
    const properties = {
      Skład: want.map((m) => m.t).join(', '),
      _zestaw: def.id,
      _staly: '1',
      _sklad: want.map((m) => `${m.h}:${m.v}`).join(','),
      _osobno: String(regular),
      _nr: Date.now().toString(36),
    };
    const added = await post(url.add(), { items: [{ id: def.variant, quantity: k, properties }] }, { sections: false });
    try {
      const res = await post(url.update(), { updates });
      render(res.sections, { fromEngine: true, highlightNew: true });
      announce(res);
    } catch (err) {
      const key = added && added.items && added.items[0] && added.items[0].key;
      if (key) await post(url.change(), { id: key, quantity: 0 }, { sections: false }).catch(() => {});
      throw err;
    }
    if (d.strings.zestawMade) {
      toast(d.strings.zestawMade.replace('__NAME__', def.name).replace('__PRICE__', money(def.unit * k)), false);
    }
    return true;
  }

  /* A set with steps, priced by count (DECISIONS 2 Nov: every tap to the cart, the count decides): SBSets.step plans and
     writes under the shared lock, from a copy it has just checked against the cart (sb-sets.js; mock and race tests in
     scripts/shop/). Returns 'stale' when the copy was old (refreshed, nothing written), true when it wrote, false when
     there was nothing to do. */
  async function stepSet(def, opts = {}) {
    const sets = window.SBSets;
    if (!sets) return false;
    const r = await sets.step(
      {
        lock: locked,
        live: async () => {
          const cart = await liveCart();
          return cart && cart.items;
        },
        data: () => state.data,
        journal,
        sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
        // a refused read ends the pass (the engine waits and tries again), never another 'stale' at once
        refresh: async () => render(await fetchSections(), { fromEngine: true }),
        add: (items) => post(url.add(), { items }, { sections: false }),
        update: async (updates, p) => {
          if (!p) return post(url.update(), { updates }, { sections: false });
          // the step the cart is priced at now: its set lines' size (the jar just added is already counted, as a single)
          const before = Math.max(0, ...state.data.lines.filter((l) => l.set === def.id).map((l) => Number(l.size) || 0));
          const over = (p.over || [])[0];
          if (p.tier > before) state.cel = { id: def.id, tier: p.tier, top: p.tier === sets.topOf(def.sizes) };
          try {
            const res = await post(url.update(), { updates });
            render(res.sections, { fromEngine: true, highlightNew: true });
            announce(res);
            const s = state.data && state.data.strings;
            if (over && s && s.zestawStock) toast(s.zestawStock.replace('__PRODUCT__', over.title).replace('__COUNT__', over.stock), false);
            return res;
          } catch (err) {
            state.cel = null;
            throw err;
          }
        },
      },
      def,
      opts
    );
    return r === 'stale' ? 'stale' : !!r;
  }

  async function sets(d) {
    if (d.codeOverSet) {
      const on = (d.codes || []).some((c) => sameCode(c, d.codeOverSet));
      if (on) return false; // his call: the better price wins, and here the code is better than the set
      const res = await post(url.update(), { attributes: { _kod_zamiast_zestawu: '' } });
      render(res.sections, { fromEngine: true });
      announce(res);
      return true;
    }
    for (const def of d.sets || []) {
      if (def.mode === 'fixed') {
        if (await fixedSet(d, def)) return true;
        continue;
      }
      if (def.mode === 'steps') {
        if (await stepSet(def)) return true;
        continue;
      }
      const setLines = d.lines.filter((l) => l.set === def.id);
      const broken = setLines.filter((l) => !l.comps.length || l.comps.length !== l.qty || l.qty % def.size);
      if (broken.length) {
        const items = groupJars(broken.filter((l) => l.comps.length).flatMap((l) => l.comps));
        if (items.length) await post(url.add(), { items }, { sections: false });
        const res = await post(url.update(), { updates: Object.fromEntries(broken.map((l) => [l.key, 0])) });
        render(res.sections, { fromEngine: true });
        announce(res);
        return true;
      }

      const singles = d.lines.filter((l) => l.elig === def.id).reverse(); // oldest first: Shopify lists the newest first
      const units = singles.reduce((sum, l) => sum + l.qty, 0);
      if (units < def.size) continue;
      const take = Math.floor(units / def.size) * def.size;
      let left = take;
      const updates = {};
      const jars = [];
      for (const l of singles) {
        if (!left) break;
        const n = Math.min(l.qty, left);
        for (let i = 0; i < n; i += 1) jars.push(l);
        updates[l.key] = l.qty - n;
        left -= n;
      }
      const counts = new Map();
      jars.forEach((l) => counts.set(l.title, (counts.get(l.title) || 0) + 1));
      const properties = {
        Skład: [...counts].map(([t, n]) => (n > 1 ? `${t} ×${n}` : t)).join(', '),
        _zestaw: def.id,
        _sklad: jars.map((l) => `${l.handle}:${l.variant}`).join(','),
        _osobno: String(jars.reduce((sum, l) => sum + l.regular, 0)),
        _nr: Date.now().toString(36),
      };
      const added = await post(url.add(), { items: [{ id: def.variant, quantity: take, properties }] }, { sections: false });
      try {
        const res = await post(url.update(), { updates });
        render(res.sections, { fromEngine: true, highlightNew: true });
        announce(res);
      } catch (err) {
        const key = added && added.items && added.items[0] && added.items[0].key;
        if (key) await post(url.change(), { id: key, quantity: 0 }, { sections: false }).catch(() => {});
        throw err;
      }
      if (d.strings.zestawMade) {
        toast(d.strings.zestawMade.replace('__NAME__', def.name).replace('__PRICE__', money(def.unit * take)), false);
      }
      return true;
    }
    return false;
  }

  /* „Usuń” next to one jar in a set: the other jars go back as single lines and the set line goes; the engine then forms
     a set again if enough jars are left. */
  function removeFromSet(line, variant, title) {
    let comps = [];
    try {
      comps = JSON.parse(line.dataset.sbComps || '[]');
    } catch (e) {}
    const i = comps.findIndex((c) => String(c.v) === String(variant));
    if (i < 0) return;
    comps.splice(i, 1);
    const key = line.dataset.key;
    let group = [];
    try {
      group = JSON.parse(line.dataset.sbGroup || '[]');
    } catch (e) {}
    if (group.length) {
      // a set by count: the jar goes and the rest is priced again by how many are left (one plan, no taking apart first)
      line.classList.add('is-busy');
      enqueue(async () => {
        try {
          const props = lineProps(line);
          // the jar's line from the newest copy; when the copy was old, it is refreshed and the jar looked up again
          let done = false;
          for (let i = 0; i < 2 && !done; i += 1) {
            const d = state.data;
            const def = d && (d.sets || []).find((x) => x.id === props._zestaw && x.mode === 'steps');
            const owner = d && d.lines.find((l) => (i ? l.set === props._zestaw : group.includes(l.key)) && l.comps.some((c) => String(c.v) === String(variant)));
            if (!def || !owner) break;
            const r = await stepSet(def, { drop: { key: owner.key, v: variant } });
            done = r === true;
          }
          if (!done) throw new Error('set');
          if (state.data && state.data.strings.zestawRemoved) toast(state.data.strings.zestawRemoved.replace('__PRODUCT__', title), false);
        } catch (err) {
          failed(err, key);
        }
      });
      return;
    }
    // A fixed set line holds `qty` sets: one set goes, its other products come back as single lines.
    const fixed = line.hasAttribute('data-sb-fixed');
    const left = fixed ? Math.max(0, Number(line.dataset.qty) - 1) : 0;
    if (!left) {
      document.querySelectorAll(`[data-sb-line][data-key="${CSS.escape(key)}"]`).forEach((l) => l.classList.add('is-leaving'));
    }
    enqueue(async () => {
      try {
        const items = groupJars(comps);
        if (items.length) await post(url.add(), { items }, { sections: false });
        const res = await post(url.change(), { id: key, quantity: left });
        render(res.sections);
        announce(res);
        if (state.data && state.data.strings.zestawRemoved) toast(state.data.strings.zestawRemoved.replace('__PRODUCT__', title), false);
      } catch (err) {
        failed(err, key);
      }
    });
  }

  /* One jar out from outside the drawer (his 3 Nov: the collection's set section, „×” on a place): the drawer's own paths, so
     prices and steps behave the same (8 → 7, 6 → 5). A jar inside a set line goes through the engine (stepSet, drop); a jar that
     is a single line loses one (cart/change, as „−”). No drawer, no burst; the same toast as in the drawer when a title is given;
     every change is announced (cartUpdate), so other parts of the page refresh. Resolves true when the cart changed.
     window.SBCart.removeJar({ set: 'mixy', variant: 46815732957510, title: 'MOTHER OF PEARL' }) */
  function removeJar(opts = {}) {
    const setId = String(opts.set || '');
    const variant = String(opts.variant || '');
    if (!variant) return Promise.resolve(false);
    return enqueue(async () => {
      for (let i = 0; i < 2; i += 1) {
        const d = state.data;
        if (!d) return false;
        const def = (d.sets || []).find((x) => x.id === setId && x.mode === 'steps');
        const owner = d.lines.find((l) => l.set === setId && (l.comps || []).some((c) => String(c.v) === variant));
        if (def && owner) {
          const r = await stepSet(def, { drop: { key: owner.key, v: variant } });
          if (r === 'stale') continue;
          if (r === true && opts.title && d.strings.zestawRemoved) toast(d.strings.zestawRemoved.replace('__PRODUCT__', opts.title), false);
          return r === true;
        }
        const single = d.lines.find((l) => !l.set && String(l.variant) === variant);
        if (single) {
          const res = await locked(() => post(url.change(), { id: single.key, quantity: Math.max(0, single.qty - 1) }));
          render(res.sections);
          announce(res);
          if (opts.title && d.strings.removed) toast(d.strings.removed.replace('__PRODUCT__', opts.title), false);
          return true;
        }
        // not in this copy of the cart: it may be old (a change made elsewhere), so once more from the shop
        if (i === 0) await refresh();
      }
      return false;
    }).catch((err) => {
      failed(err);
      return false;
    });
  }
  window.SBCart = Object.assign(window.SBCart || {}, { removeJar });

  /* ---------------------------------------------------- e-book: consent to delivery at once */

  /* His decision 23 Oct and rg-6 (2 Nov), terms § 13(5): the shopper asks for the e-book at once and acknowledges losing the right
     to withdraw. On the product page (snippet sb-ebook-consent) the box joins the product form and „Dodaj do koszyka” stays off
     until it is ticked; the tick reaches the order as a line property. */
  const CONSENT = 'Zgoda na natychmiastowe dostarczenie';
  function bindConsent() {
    document.querySelectorAll('[data-sb-ebook-consent]').forEach((wrap) => {
      if (wrap.sbBound) return;
      // the product form itself (Dawn also has an installments form with the same action), and its id read as an attribute
      // (form.id is the form's <input name="id">)
      const scope = wrap.closest('product-info, .product__info-container, section') || document;
      const pf = scope.querySelector('product-form');
      const form = pf && pf.querySelector('form[action*="/cart/add"]');
      const input = wrap.querySelector('input');
      if (!form || !input || !form.getAttribute('id')) return;
      const btn = pf.submitButton || form.querySelector('[type="submit"]');
      if (!btn || btn.disabled) return; // sold out: the theme keeps it off already
      wrap.sbBound = true;
      input.setAttribute('form', form.getAttribute('id'));
      const sync = () => {
        btn.disabled = !input.checked;
        if (input.checked) btn.removeAttribute('aria-disabled');
        else btn.setAttribute('aria-disabled', 'true');
      };
      input.addEventListener('change', sync);
      sync();
    });
  }
  bindConsent();

  /* ---------------------------------------------------- terms + checkout guard */

  // an e-book line without the consent to delivery at once (rg-6) keeps checkout and express pay shut, like the terms
  const consentMissing = () => !!document.querySelector('[data-sb-cart] [data-sb-consent]:not(:checked)');

  function restoreTerms() {
    const terms = store.get('sb-terms') === '1';
    const ok = terms && !consentMissing();
    document.querySelectorAll('[data-sb-terms]').forEach((box) => (box.checked = terms));
    document.querySelectorAll('[data-sb-cta]').forEach((cta) => {
      cta.setAttribute('aria-disabled', String(!ok));
      // the pale button says why inside itself (review 3 Nov: no reason anywhere); its height does not change
      cta.dataset.why = !terms ? 'terms' : ok ? '' : 'consent';
    });
    document.querySelectorAll('[data-sb-express]').forEach((box) => {
      box.inert = !ok;
      box.classList.toggle('is-locked', !ok);
    });
  }

  function expectedAction() {
    return new URL(cartUrl(), location.origin);
  }

  function guardForms() {
    document.querySelectorAll('[data-sb-checkout]').forEach((form) => {
      checkForm(form);
      if (form.sbObserved) return;
      form.sbObserved = true;
      new MutationObserver(() => checkForm(form)).observe(form, {
        attributes: true,
        subtree: true,
        attributeFilter: ['action', 'method', 'target', 'formaction', 'formmethod', 'formtarget'],
      });
    });
  }

  function checkForm(form) {
    const want = expectedAction();
    let changed = false;
    const action = new URL(form.getAttribute('action') || '', location.origin);
    if (action.origin !== location.origin || action.pathname !== want.pathname) {
      form.setAttribute('action', want.pathname);
      changed = true;
    }
    if ((form.getAttribute('method') || '').toLowerCase() !== 'post') {
      form.setAttribute('method', 'post');
      changed = true;
    }
    if (form.hasAttribute('target')) {
      form.removeAttribute('target');
      changed = true;
    }
    form.querySelectorAll('[formaction],[formmethod],[formtarget]').forEach((el) => {
      ['formaction', 'formmethod', 'formtarget'].forEach((a) => el.removeAttribute(a));
      changed = true;
    });
    if (changed) {
      console.warn('[sb-cart] checkout form was changed by another script and has been restored');
      document.dispatchEvent(new CustomEvent('sb:guard', { detail: { at: Date.now() } }));
    }
  }

  /* What is missing before checkout or express pay: the e-book consent, then the terms. Says so, shakes the terms box and puts the
     focus there. `express`: a tap on the locked wallet, which gets its own sentence. Returns true when something was missing. */
  function askFirst(form, express) {
    const missing = document.querySelector('[data-sb-cart] [data-sb-consent]:not(:checked)');
    if (missing) {
      document.querySelectorAll('[data-sb-consent-warning]').forEach((w) => (w.hidden = false));
      missing.focus();
      return true;
    }
    const box = form.querySelector('[data-sb-terms]');
    const warn = form.querySelector(express ? '[data-sb-express-warning]' : '[data-sb-terms-warning]');
    const other = form.querySelector(express ? '[data-sb-terms-warning]' : '[data-sb-express-warning]');
    if (!box || !box.checked) {
      if (other) other.hidden = true;
      if (warn) warn.hidden = false;
      const tile = box && box.closest('.sb-cart__terms');
      if (tile) {
        tile.classList.remove('is-warn');
        void tile.offsetWidth; // restart the shake on every try
        tile.classList.add('is-warn');
      }
      if (box) box.focus();
      return true;
    }
    return false;
  }

  async function checkout(form) {
    if (askFirst(form, false)) return;
    const cta = form.querySelector('[data-sb-cta]');
    if (cta) {
      cta.classList.add('is-loading');
      cta.setAttribute('aria-busy', 'true');
    }
    for (const [key, timer] of state.qtyTimers) {
      clearTimeout(timer);
      state.qtyTimers.delete(key);
      const line = document.querySelector(`[data-sb-line][data-key="${CSS.escape(key)}"]`);
      const base = state.qtyBase.get(key);
      state.qtyBase.delete(key);
      if (line && base !== undefined) sendQty(key, Number(line.dataset.qty) - base, line.dataset.max ? Number(line.dataset.max) : Infinity);
    }
    await state.queue;
    await enqueue(engine);
    await state.queue;
    const priced = state.data && state.data.lines.some((l) => l.gift && l.line > 0);
    if (priced) {
      if (cta) cta.classList.remove('is-loading');
      return;
    }
    checkForm(form);
    const flag = document.createElement('input');
    flag.type = 'hidden';
    flag.name = 'checkout';
    flag.value = '1';
    form.appendChild(flag);
    HTMLFormElement.prototype.submit.call(form);
  }

  /* ---------------------------------------------------------------- refresh */

  async function refresh() {
    const sections = await fetchSections().catch(() => null);
    if (sections) render(sections);
  }

  function scheduleRefresh(delay = 150) {
    clearTimeout(state.refreshTimer);
    state.refreshTimer = setTimeout(() => enqueue(refresh), delay);
  }

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (CART_WRITE.test(entry.name.split('#')[0]) && !entry.name.includes(OWN_MARK)) {
            scheduleRefresh();
            return;
          }
        }
      }).observe({ type: 'resource', buffered: false });
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- drawer */

  class SbCartDrawer extends HTMLElement {
    connectedCallback() {
      this.isPage = this.hasAttribute('data-sb-page');
      if (!this.isPage) {
        this.bindHeaderIcon();
        this.bindSwipe();
      }
    }

    /* his 4 Nov: the drawer also closes with a swipe to the right, not only with ×. Once a move is clearly sideways and to the
       right, the panel follows the finger; let go past a third of its width (or after a quick flick) and it closes, otherwise
       it springs back. Vertical scrolling, any sideways scroller inside the cart and text fields are left alone. Listeners sit
       on this element, so the rest of the page keeps passive scrolling. */
    bindSwipe() {
      if (this.sbSwipe) return;
      this.sbSwipe = true;
      let s = null;
      const sideScroller = (el, panel) => {
        for (; el && el !== panel; el = el.parentElement) {
          if (el.scrollWidth > el.clientWidth + 1) {
            const o = getComputedStyle(el).overflowX;
            if (o === 'auto' || o === 'scroll') return true;
          }
        }
        return false;
      };
      this.addEventListener(
        'touchstart',
        (e) => {
          s = null;
          if (!this.classList.contains('is-open') || e.touches.length !== 1) return;
          const panel = this.querySelector('[data-sb-panel]');
          const t = e.target;
          if (!panel || !panel.contains(t) || (t.closest && t.closest('textarea, input:not([type=checkbox])')) || sideScroller(t, panel)) return;
          const p = e.touches[0];
          s = { panel, x: p.clientX, y: p.clientY, lastX: p.clientX, lastT: e.timeStamp, v: 0, dx: 0, mode: '' };
        },
        { passive: true }
      );
      this.addEventListener(
        'touchmove',
        (e) => {
          if (!s || e.touches.length !== 1) return;
          const p = e.touches[0];
          const dx = p.clientX - s.x;
          const dy = p.clientY - s.y;
          if (!s.mode) {
            if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
            s.mode = dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.5 ? 'swipe' : 'off';
            if (s.mode === 'swipe') s.panel.style.transition = 'none';
          }
          if (s.mode !== 'swipe') return;
          if (e.cancelable) e.preventDefault();
          const dt = Math.max(1, e.timeStamp - s.lastT);
          s.v = (p.clientX - s.lastX) / dt;
          s.lastX = p.clientX;
          s.lastT = e.timeStamp;
          s.dx = Math.max(0, dx);
          s.panel.style.transform = 'translateX(' + s.dx + 'px)';
        },
        { passive: false }
      );
      const end = () => {
        const g = s;
        s = null;
        if (!g || g.mode !== 'swipe') return;
        g.panel.style.transition = '';
        g.panel.style.transform = '';
        if (g.dx > g.panel.offsetWidth / 3 || (g.dx > 40 && g.v > 0.4)) this.close();
      };
      this.addEventListener('touchend', end, { passive: true });
      this.addEventListener('touchcancel', end, { passive: true });
    }

    bindHeaderIcon() {
      const link = document.getElementById('cart-icon-bubble');
      if (!link || link.sbBound) return;
      link.sbBound = true;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      const toggle = () => (this.classList.contains('is-open') ? this.close() : this.open(link));
      link.addEventListener('click', (e) => {
        e.preventDefault();
        toggle();
      });
      link.addEventListener('keydown', (e) => {
        if (e.code === 'Space') {
          e.preventDefault();
          toggle();
        }
      });
    }

    getSectionsToRender() {
      return SECTIONS.map((id) => ({ id }));
    }

    setActiveElement(el) {
      this.activeElement = el;
    }

    renderContents(parsed) {
      render(parsed && parsed.sections, { highlightNew: true });
      announce(parsed);
      if (notePage()) addedNote(parsed);
      else if (this.dataset.openOnAdd !== 'false') this.open();
    }

    /* Not modal (his 2 Nov: „jak kliknę gdzieś na stronę poza koszykiem to koszyk powinien automatycznie się chować”, and the
       shop must keep working while it is open): the page is not locked and focus is not trapped. Focus goes into the panel
       when it opens; a click anywhere outside closes it and still does what it was meant for (the next „Dodaj” adds); Esc
       closes it from anywhere and gives focus back to what opened it. */
    open(trigger) {
      if (this.isPage) return;
      if (trigger) this.activeElement = trigger;
      fitAboveBanner();
      clearInterval(this.bannerTimer);
      this.bannerTimer = setInterval(fitAboveBanner, 500);
      this.classList.add('is-open');
      const panel = this.querySelector('[data-sb-panel]');
      if (panel) {
        setTimeout(() => {
          if (this.classList.contains('is-open')) (panel.querySelector('[data-sb-close]') || panel).focus({ preventScroll: true });
        }, reduceMotion ? 0 : 300);
      }
      if (Date.now() - state.lastRefresh > 2000) scheduleRefresh(0);
    }

    close(giveFocusBack = true) {
      if (this.isPage || !this.classList.contains('is-open')) return;
      clearInterval(this.bannerTimer);
      this.classList.remove('is-open');
      const panel = this.querySelector('[data-sb-panel]');
      const inside = panel && panel.contains(document.activeElement);
      if (giveFocusBack && inside && this.activeElement && this.activeElement.isConnected) this.activeElement.focus({ preventScroll: true });
      else if (inside) document.activeElement.blur();
    }
  }
  customElements.define('cart-drawer', SbCartDrawer);

  // a press anywhere outside the open drawer closes it, and the press goes on to what it was on (no layer eats it)
  document.addEventListener(
    'pointerdown',
    (e) => {
      const drawer = document.querySelector('cart-drawer.is-open');
      const t = e.target;
      if (!drawer || drawer.isPage || !t.closest) return;
      if (t.closest('[data-sb-panel], [data-sb-toast], [data-sb-tray], #cart-icon-bubble')) return;
      drawer.close(false);
    },
    true
  );
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const drawer = document.querySelector('cart-drawer.is-open');
    if (drawer && !drawer.isPage) drawer.close();
  });

  /* Pandectes' cookie banner sits at the top z-index and must stay usable: while it overlaps the drawer, the drawer
     ends right above it, so the terms box and the checkout button are never hidden. */
  function fitAboveBanner() {
    const panel = document.querySelector('.sb-drawer [data-sb-panel]');
    if (!panel) return;
    const banner = document.querySelector('.pd-cookie-banner-window');
    let inset = 0;
    const style = banner && getComputedStyle(banner);
    if (banner && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0) {
      const b = banner.getBoundingClientRect();
      const left = window.innerWidth - panel.offsetWidth;
      const overlapsX = b.right > left && b.left < window.innerWidth;
      if (overlapsX && b.height > 0 && b.bottom >= window.innerHeight - 2) inset = Math.max(0, window.innerHeight - b.top);
    }
    panel.style.setProperty('--sb-bottom-inset', `${Math.round(inset)}px`);
    // A short panel (banner showing, phone in landscape) keeps the footer to terms, checkout and express pay.
    panel.classList.toggle('is-short', window.innerHeight - inset < 640);
  }
  new MutationObserver(() => fitAboveBanner()).observe(document.body, { childList: true, subtree: false });
  window.addEventListener('resize', () => fitAboveBanner());
  document.addEventListener('click', () => setTimeout(fitAboveBanner, 400));

  /* ---------------------------------------------------------------- events */

  document.addEventListener(
    'pointerdown',
    (e) => {
      const t = e.target;
      const item = t && t.closest && t.closest('[data-sb-cart]') && t.closest(FLIP_ITEMS);
      if (item) state.touch = { key: flipKey(item), at: Date.now() };
    },
    true
  );
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!d.matches || !d.matches('details.sb-line__set') || !d.isConnected) return;
      const kind = d.closest('[data-sb-set-kind]');
      if (!kind) return;
      if (d.open) openSets.add(kind.dataset.sbSetKind);
      else openSets.delete(kind.dataset.sbSetKind);
      saveOpenSets();
    },
    true
  );
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t.closest) return;
    const inCart = t.closest('[data-sb-cart]');
    const close = t.closest('[data-sb-close]');
    if (close) {
      const drawer = close.closest('cart-drawer');
      if (drawer && !drawer.isPage) {
        e.preventDefault();
        drawer.close();
      }
      return;
    }
    if (t.closest('[data-sb-code-remove]')) {
      removeCode(t.closest('[data-sb-code-remove]').dataset.sbCodeRemove);
      return;
    }
    if (t.closest('[data-sb-tray-cart]')) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && !drawer.isPage) drawer.open(t.closest('[data-sb-tray-cart]'));
      else window.location.href = cartUrl();
      return;
    }
    const info = t.closest('[data-sb-info]');
    if (info) {
      // the (i) next to the named invoice: what it means, under the line
      const box = info.closest('.sb-cart__invoice');
      const on = !!box && !box.classList.contains('is-info');
      if (box) box.classList.toggle('is-info', on);
      info.setAttribute('aria-expanded', String(on));
      return;
    }
    if (t.closest('[data-sb-undo]')) {
      hideToast();
      const u = state.undo;
      state.undo = null;
      if (u) enqueue(() => locked(() => post(url.add(), { items: Array.isArray(u) ? u : [u] })).then((res) => { render(res.sections, { highlightNew: true }); announce(res); }));
      return;
    }
    if (!inCart) return;
    const line = t.closest('[data-sb-line]');
    if (t.closest('[data-sb-inc]') && line) changeQty(line, 1);
    else if (t.closest('[data-sb-dec]') && line) changeQty(line, -1);
    else if (t.closest('[data-sb-set-remove]') && line) {
      const btn = t.closest('[data-sb-set-remove]');
      removeFromSet(line, btn.dataset.sbSetRemove, btn.dataset.sbSetTitle || '');
    } else if (t.closest('[data-sb-remove]') && line) removeLine(line);
    else if (t.closest('[data-sb-add]')) addVariant(t.closest('[data-sb-add]'));
    else if (t.closest('[data-sb-cta]')) {
      e.preventDefault();
      checkout(t.closest('form'));
    } else if (t.classList.contains('sb-cart__foot')) {
      // the locked wallet is inert, so a tap on it lands on the footer: if it was on the wallet, say why it is pale
      const express = t.querySelector('[data-sb-express].is-locked');
      const r = express && express.getBoundingClientRect();
      const form = t.querySelector('[data-sb-checkout]');
      if (r && form && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) askFirst(form, true);
    }
  });

  /* ---------------------------------------------------- „Dodano do koszyka” */

  /* His 4 Nov: „koszyk nie musi otwierać się po każdym dodaniu, ale powinien na ekranie mały box się pokazywać z informacją dodano
     do koszyka: 1 x "nazwa produktu"”. On a page with the set section (ZESTAW Z POSYPEK, GLITERY, the GLITER landing: the section
     shows what the set did) an add shows a small note for 2,6 s with „Zobacz koszyk” instead of opening the drawer; elsewhere the
     drawer opens as before. The note goes at the bottom, above the cookie button, next to the „Dodaj” just pressed or under the
     header: the first place that covers least (the set's bar and places weigh ten times the rest), measured when it shows and
     while the page scrolls. Pointing at it or focusing it keeps it.
     A failed add says that it failed. The live region is always in the page, so screen readers hear each note. */
  const notePage = () => !!document.querySelector('[data-sbz]');
  const NOTE_MS = 2600;
  // the set's bar and places must stay clear; the cookie button and the „Dodaj” just pressed better too (they weigh less)
  const AVOID = '[data-sbz] .sbz-bar, [data-sbz] .sbz-slots';
  const SPARE = '.cc-revoke';

  function noteWrap() {
    let wrap = document.querySelector('[data-sb-added]');
    if (wrap) return wrap;
    wrap = document.createElement('section');
    wrap.className = 'sb-added-wrap';
    wrap.setAttribute('data-sb-added', '');
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-live', 'polite');
    document.body.appendChild(wrap);
    wrap.addEventListener('click', (e) => {
      const view = e.target.closest && e.target.closest('[data-sb-added-view]');
      if (!view) return;
      const drawer = document.querySelector('cart-drawer[data-sb-drawer]');
      hideNote(true);
      if (drawer && typeof drawer.open === 'function') drawer.open(view);
    });
    ['pointerenter', 'focusin'].forEach((ev) => wrap.addEventListener(ev, () => clearTimeout(state.noteHide)));
    ['pointerleave', 'focusout'].forEach((ev) =>
      wrap.addEventListener(ev, () => {
        clearTimeout(state.noteHide);
        state.noteHide = setTimeout(() => hideNote(), NOTE_MS);
      })
    );
    window.addEventListener('scroll', () => wrap.firstElementChild && requestAnimationFrame(() => placeNote(wrap)), { passive: true });
    window.addEventListener('resize', () => wrap.firstElementChild && placeNote(wrap));
    return wrap;
  }

  function placeNote(wrap) {
    const note = wrap.firstElementChild;
    if (!note) return;
    const vh = window.innerHeight;
    const h = note.offsetHeight;
    const nr = note.getBoundingClientRect();
    const seen = (r) => r.width && r.height && r.bottom > 0 && r.top < vh;
    const rects = [
      ...[...document.querySelectorAll(AVOID)].map((el) => ({ r: el.getBoundingClientRect(), w: 10 })),
      ...[...document.querySelectorAll(SPARE), state.addFrom].filter((el) => el && el.isConnected).map((el) => ({ r: el.getBoundingClientRect(), w: 1 })),
    ].filter((x) => seen(x.r));
    const head = document.querySelector('.section-header');
    const headBottom = head ? Math.max(0, head.getBoundingClientRect().bottom) : 0;
    const cookie = document.querySelector('.cc-revoke');
    const cookieTop = cookie && cookie.offsetHeight ? cookie.getBoundingClientRect().top : vh;
    const from = state.addFrom && state.addFrom.isConnected ? state.addFrom.getBoundingClientRect() : null;
    const spots = [vh - 16 - h, cookieTop - 8 - h, ...(from ? [from.top - 8 - h, from.bottom + 8] : []), headBottom + 12];
    const hits = (top) =>
      rects.reduce((sum, { r, w }) => {
        const x = Math.max(0, Math.min(nr.right, r.right) - Math.max(nr.left, r.left));
        const y = Math.max(0, Math.min(top + h, r.bottom) - Math.max(top, r.top));
        return sum + x * y * w;
      }, 0);
    const best = spots.filter((t) => t >= headBottom && t + h <= vh).reduce((a, t) => (hits(t) < hits(a) ? t : a), vh - 16 - h);
    wrap.style.top = `${Math.round(best)}px`;
  }

  function showNote(text, ok) {
    const wrap = noteWrap();
    const s = (state.data && state.data.strings) || {};
    clearTimeout(state.noteHide);
    wrap.innerHTML = `<div class="sb-added${ok ? '' : ' is-error'}">
        <span class="sb-added__mark" aria-hidden="true">${ok ? '✓' : '!'}</span>
        <p class="sb-added__text">${escapeHtml(text)}</p>
        ${ok && s.addedView ? `<button type="button" class="sb-added__view" data-sb-added-view>${escapeHtml(s.addedView)}</button>` : ''}
      </div>`;
    placeNote(wrap);
    requestAnimationFrame(() => wrap.firstElementChild && wrap.firstElementChild.classList.add('is-on'));
    state.noteHide = setTimeout(() => hideNote(), ok ? NOTE_MS : NOTE_MS + 1400);
  }

  function hideNote(now) {
    const wrap = document.querySelector('[data-sb-added]');
    const note = wrap && wrap.firstElementChild;
    clearTimeout(state.noteHide);
    if (!note) return;
    note.classList.remove('is-on');
    setTimeout(() => note.isConnected && note.remove(), now || reduceMotion ? 0 : 220);
  }

  // what Shopify answered to /cart/add: one line (Dawn's form) or { items } (several at once); the count is what was asked for
  function addedNote(parsed) {
    const s = (state.data && state.data.strings) || {};
    const lines = parsed && (parsed.items || (parsed.product_title ? [parsed] : []));
    if (!lines || !lines.length || !s.added) return;
    const one = lines.length === 1 ? state.addQty || 1 : 1;
    state.addQty = 0;
    const names = lines.map((l) => {
      const v = l.variant_title && !/^default title$/i.test(l.variant_title) ? ` (${l.variant_title})` : '';
      return `${one}\u00a0×\u00a0${l.product_title}${v}`;
    });
    showNote(s.added.replace('__ITEMS__', names.join(', ')), true);
  }

  function failedNote(reason) {
    const s = (state.data && state.data.strings) || {};
    state.addQty = 0;
    showNote(reason || s.addedFailed || s.error || '', false);
  }

  // Dawn's own error answer (sold out, more than in stock): on a set page the drawer stays shut, so the note says it
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartError, (ev) => {
      if (!notePage()) return;
      const s = (state.data && state.data.strings) || {};
      const why = ev && (typeof ev.errors === 'string' ? ev.errors : ev.message);
      failedNote(why ? `${s.addedFailed || ''} ${why}`.trim() : s.addedFailed);
    });
  }

  /* „Dodaj do koszyka” on a product (Dawn's product-form, the quick add on cards, the floating button): the add waits for
     the shared lock, so it never lands between the two writes of an engine pass (2 Nov race test: a jar added to a line the
     engine was about to empty was lost). Dawn's own handler still does the add, the drawer and Shopify's events; the lock is
     let go once its button stops loading. */
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      const pf = form && form.closest && form.closest('product-form');
      if (!pf || typeof pf.onSubmitHandler !== 'function' || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const btn = pf.submitButton || form.querySelector('[type="submit"]');
      state.addQty = Number(new FormData(form).get('quantity')) || 1;
      state.addFrom = btn;
      /* Dawn answers an add with cartUpdate or cartError; at 429 the shop's reply is not JSON and Dawn only logs it, so the
         button stopped spinning and nothing was said (review 3 Nov). No answer at all = the add did not land: say so under
         the button. */
      let answered = false;
      const pubsub = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
      const unsubs = pubsub ? [PUB_SUB_EVENTS.cartUpdate, PUB_SUB_EVENTS.cartError].map((ev) => subscribe(ev, () => (answered = true))) : [];
      locked(
        () =>
          new Promise((resolve) => {
            const started = Date.now();
            pf.onSubmitHandler(e);
            const wait = () => {
              const loading = btn && btn.classList.contains('loading');
              if (!loading || Date.now() - started > 10000) resolve();
              else setTimeout(wait, 60);
            };
            setTimeout(wait, 60);
          })
      ).then(() => {
        unsubs.forEach((off) => typeof off === 'function' && off());
        const s = (state.data && state.data.strings) || {};
        if (!pubsub || answered || !s.busyAdd) return;
        if (typeof pf.handleErrorMessage === 'function') pf.handleErrorMessage(s.busyAdd);
        if (notePage()) failedNote(s.busyAdd);
      });
    },
    true
  );

  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('[data-sb-code-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('[data-sb-code-input]');
    applyCode(input && input.value);
  });

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target.closest && e.target.closest('[data-sb-checkout]');
      if (!form) return;
      e.preventDefault();
      checkout(form);
    },
    true
  );

  function composeNote(cart) {
    const box = cart.querySelector('[data-sb-invoice]');
    const text = (cart.querySelector('[data-sb-note]') || {}).value || '';
    return [box && box.checked ? box.dataset.flag : '', text.trim()].filter(Boolean).join('\n');
  }

  function saveNote(cart, delay) {
    clearTimeout(state.noteTimer);
    state.noteTimer = setTimeout(() => {
      const value = composeNote(cart);
      enqueue(() =>
        post(url.update(), { note: value }, { sections: false }).then(() => {
          document.querySelectorAll('[data-sb-note],[data-sb-invoice]').forEach((n) => (n.dataset.dirty = ''));
          document.querySelectorAll('[data-sb-note-saved]').forEach((s) => {
            s.textContent = state.data ? state.data.strings.noteSaved : '';
          });
          clearTimeout(state.noteSavedTimer);
          state.noteSavedTimer = setTimeout(() => {
            document.querySelectorAll('[data-sb-note-saved]').forEach((s) => (s.textContent = ''));
          }, 2500);
        })
      );
    }, delay);
  }

  document.addEventListener('change', (e) => {
    const invoice = e.target.closest && e.target.closest('[data-sb-invoice]');
    if (invoice) {
      invoice.dataset.dirty = '1';
      saveNote(invoice.closest('[data-sb-cart]'), 0);
      return;
    }
    const consent = e.target.closest && e.target.closest('[data-sb-consent]');
    if (consent) {
      // the cart's e-book line gets (or loses) the consent as its line property
      const line = consent.closest('[data-sb-line]');
      if (!line) return;
      const key = line.dataset.key;
      document.querySelectorAll('[data-sb-consent-warning]').forEach((w) => (w.hidden = true));
      enqueue(() =>
        locked(() =>
          post(url.change(), {
            id: key,
            quantity: Number(line.dataset.qty) || 1,
            properties: consent.checked ? { [CONSENT]: 'Tak' } : {},
          })
        )
          .then((res) => {
            render(res.sections);
            announce(res);
          })
          .catch((err) => failed(err, key))
      );
      restoreTerms();
      return;
    }
    const box = e.target.closest && e.target.closest('[data-sb-terms]');
    if (!box) return;
    store.set('sb-terms', box.checked ? '1' : '0');
    document.querySelectorAll('[data-sb-terms-warning],[data-sb-express-warning]').forEach((w) => (w.hidden = true));
    document.querySelectorAll('.sb-cart__terms.is-warn').forEach((t) => t.classList.remove('is-warn'));
    restoreTerms();
  });

  document.addEventListener('input', (e) => {
    const note = e.target.closest && e.target.closest('[data-sb-note]');
    if (!note) return;
    note.dataset.dirty = '1';
    saveNote(note.closest('[data-sb-cart]'), 600);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || (event.source !== 'sb-cart' && event.source !== 'product-form')) scheduleRefresh();
    });
  }
  ['shopify:cart:lines-update', 'shopify:cart:note-update', 'shopify:cart:attributes-update', 'shopify:cart:discount-update'].forEach(
    (name) => document.addEventListener(name, () => scheduleRefresh())
  );
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - state.lastRefresh > 2000) scheduleRefresh(0);
  });
  window.addEventListener('pageshow', (e) => e.persisted && scheduleRefresh(0));
  document.addEventListener('prerenderingchange', () => scheduleRefresh(0));
  try {
    state.channel = new BroadcastChannel('sb-cart');
    state.channel.onmessage = () => scheduleRefresh(0);
  } catch (e) {}

  /* The „miękkie pałeczki” hanger (his question 3 Nov, my pick B from the variants he was shown): it swings once as it comes into
     view, and again under the mouse (CSS). Not on a loop: a product page is looked at for a while. Nothing with reduced motion. */
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const swing = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          swing.unobserve(e.target);
          e.target.classList.add('is-swing');
          e.target.addEventListener('animationend', () => e.target.classList.remove('is-swing'), { once: true });
        }),
      { threshold: 0.6 }
    );
    const watch = (root) => root.querySelectorAll('.sb-zawieszka').forEach((z) => swing.observe(z));
    watch(document);
    document.addEventListener('shopify:section:load', (e) => watch(e.target));
  }

  state.data = readData();
  state.lastRefresh = Date.now();
  afterRender();
  enqueue(engine);
})();
